import {
  SBackButton,
  SButton,
  SText,
  SETVIImage
} from '@setvi/shared/components';
import { useDialog } from '@setvi/shared/providers';
import { Box, styled } from '@material-ui/core';
import Feedback, {
  FEEDBACK_DIALOG,
  FeedbackValues
} from 'componentsV2/ai/feedback';
import { sendMessageToSlackChannel } from '@setvi/shared/services';

import { Group } from 'pages/auto-rfq/styled';
import { useCallback } from 'react';
import { SlackMessageTypes } from '@setvi/shared/enums';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { useRFQStore } from 'pages/auto-rfq/store';
import { Product } from '@setvi/shared/interfaces';
import { ExportMenu } from 'pages/auto-rfq/pages/details/components';
import { SettingsOutlined } from '@material-ui/icons';
import {
  QuoteDetailsModal,
  QUOTE_DETAILS_DIALOG
} from 'pages/auto-rfq/pages/details/components/quote-details-modal';
import { useTooltipStyles } from 'pages/auto-rfq/pages/details/components/quote-details-modal/style';
import { useQuoteStore } from 'pages/quotes/store';

export const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gridGap: 12,
  padding: theme.spacing(3, 6, 3, 4)
}));

interface RFQHeaderProps {
  currentJobId?: string;
  title: string;
  hideDownload?: boolean;
  hideDetails?: boolean;
  competitor?: string;
  selected?: Product[];
  triggerBackDialog?: () => void;
}

const RFQHeader = ({
  title,
  currentJobId,
  competitor,
  hideDetails,
  hideDownload,
  selected,
  triggerBackDialog
}: RFQHeaderProps) => {
  const { openDialog, closeDialog } = useDialog();
  const { user } = useAppContext();
  const queryClient = useQueryClient();
  const { files, quoteId } = useRFQStore();
  const tooltipClasses = useTooltipStyles();
  const { quote, updateQuoteInfo } = useQuoteStore();

  const handleFeedbackSubmit = useCallback(
    async ({ feedback, rating }: FeedbackValues) => {
      const body = {
        _id: process.env.SLACK_CHANNEL_ID,
        type: SlackMessageTypes.AutoRFQ,
        ...(rating ? { rating } : {}),
        feedback,
        user: {
          email: user?.Email,
          userId: user?.ID,
          company: user?.CompanyName,
          fullName: user?.FullName
        },
        results: {
          type: 'file',
          data: selected?.slice(0, 10) || [],
          value: files?.[0]?.file.url || ''
        }
      };

      try {
        await queryClient.fetchQuery({
          queryKey: ['sendFeedback'],
          queryFn: () => sendMessageToSlackChannel().mutationFn(body)
        });

        closeDialog(FEEDBACK_DIALOG);
        enqueueSnackbar('Feedback sent successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar('Failed to send feedback', {
          variant: 'error',
          autoHideDuration: 8000
        });
      }
    },
    [user, selected, files, queryClient, closeDialog]
  );

  const triggerFeedback = useCallback(() => {
    openDialog(
      <Feedback
        label="How can we improve AUTO RFQ"
        onClose={() => closeDialog(FEEDBACK_DIALOG)}
        handleSubmit={handleFeedbackSubmit}
        loading={false}
      />,
      { id: FEEDBACK_DIALOG }
    );
  }, [openDialog, closeDialog, handleFeedbackSubmit]);

  const triggerQuoteDetails = useCallback(() => {
    openDialog(
      <QuoteDetailsModal
        quote={quote}
        updateQuoteInfo={updateQuoteInfo}
        onClose={() => closeDialog(QUOTE_DETAILS_DIALOG)}
      />,
      { id: QUOTE_DETAILS_DIALOG }
    );
  }, [openDialog, quote, updateQuoteInfo, closeDialog]);

  return (
    <Header>
      <Group>
        <SBackButton onClick={triggerBackDialog} />

        <SText weight="bold" size="xl" title={title} />
        {!!competitor && <SText weight="medium" title={`(${competitor})`} />}
      </Group>

      {!!currentJobId && process.env.NODE_ENV === 'development' && (
        <SText size="sm" weight="medium" title={`Job ID: ${currentJobId}`} />
      )}

      <Group>
        <SButton
          variant="text"
          onClick={triggerFeedback}
          startIcon={<SETVIImage src="/icons/feedback.svg" alt="Feedback" />}
          style={{
            color: '#5D7083',
            borderRadius: 36
          }}>
          Feedback
        </SButton>

        {!!quoteId && !hideDetails && (
          <SButton
            variant="outlined"
            style={{
              borderRadius: 120
            }}
            tooltipProps={{
              classes: tooltipClasses
            }}
            tooltipTitle="Update quote information, notes, and contact details."
            onClick={triggerQuoteDetails}
            startIcon={<SettingsOutlined style={{ color: '#1E96FC' }} />}>
            Details
          </SButton>
        )}

        {!hideDownload && <ExportMenu />}
      </Group>
    </Header>
  );
};

export default RFQHeader;
